/** Dates au format AAAA-MM-JJ, tel que stocké et synchronisé. */

/*
 * Voyage dans le temps — DÉVELOPPEMENT UNIQUEMENT.
 *
 * Une app dont tout dépend du cycle est impossible à éprouver sans pouvoir
 * avancer la date : la clôture de fin de mois, le revenu reproposé, les
 * journées en retard ne se déclenchent qu'au passage d'une frontière. Attendre
 * le 1er du mois pour tester n'est pas une méthode.
 *
 * Le décalage est en JOURS et vit dans localStorage. `import.meta.env.DEV` est
 * remplacé par `false` à la compilation : tout ce bloc disparaît du paquet
 * livré, et `today()` y redevient la date du jour, sans branche ni coût.
 */
const DAY_SHIFT = 'ariariko:dev_day_shift'

export function getDayShift(): number {
  if (!import.meta.env.DEV) return 0
  try { return Number(localStorage.getItem(DAY_SHIFT) ?? 0) || 0 } catch { return 0 }
}

export function setDayShift(days: number): void {
  try { localStorage.setItem(DAY_SHIFT, String(Math.trunc(days))) } catch { /* privée */ }
}

/** L'instant courant, décalage de test compris. */
export function now(): Date {
  const date = new Date()
  const shift = getDayShift()
  if (shift !== 0) date.setDate(date.getDate() + shift)
  return date
}

export function today(): string {
  return toIsoDate(now())
}

export function toIsoDate(date: Date): string {
  // Volontairement pas `toISOString()` : elle convertit en UTC, ce qui décale
  // d'un jour toute saisie faite après 21 h à Madagascar (UTC+3).
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

const LONG = new Intl.DateTimeFormat('fr-MG', { day: 'numeric', month: 'long' })
/* Avec l'année : sur une app d'argent, l'historique se garde des années, et
   « 25 août » seul devient ambigu dès le deuxième janvier. */
const LONG_YEAR = new Intl.DateTimeFormat('fr-MG',
  { day: 'numeric', month: 'long', year: 'numeric' })
const MONTH = new Intl.DateTimeFormat('fr-MG', { month: 'long', year: 'numeric' })
const WEEKDAY = new Intl.DateTimeFormat('fr-MG', { weekday: 'long' })
const FULL = new Intl.DateTimeFormat('fr-MG', {
  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
})

function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d)
}

/** L'écart en jours entre une date et aujourd'hui. Négatif dans le futur. */
function daysAgo(iso: string): number {
  const from = parseIsoDate(iso).getTime()
  const to = parseIsoDate(today()).getTime()
  // `round` et non `floor` : les deux bornes sont à minuit local, mais un
  // changement d'heure décalerait le quotient de quelques heures.
  return Math.round((to - from) / 86_400_000)
}

/** « Aujourd'hui », « Hier », sinon « 14 août 2026 ». */
export function formatDay(iso: string): string {
  // `now()` et non `new Date()` : sans ça, le voyage dans le temps affiche
  // « Hier » sur la veille RÉELLE, pas sur celle que l'app croit vivre.
  const ecart = daysAgo(iso)
  if (ecart === 0) return "Aujourd'hui"
  if (ecart === 1) return 'Hier'
  return LONG_YEAR.format(parseIsoDate(iso))
}

/** « août 2026 » */
export function formatMonth(iso: string): string {
  return MONTH.format(parseIsoDate(iso))
}

/** Le mois d'une date ISO, sous forme « AAAA-MM ». */
export function monthOf(iso: string): string {
  return iso.slice(0, 7)
}

/** « lundi ». Le repère le plus utile quand on planifie une semaine. */
export function weekdayName(iso: string): string {
  return WEEKDAY.format(parseIsoDate(iso))
}

/** « 24 août » — sans le raccourci « Aujourd'hui » de `formatDay`. */
export function formatShortDate(iso: string): string {
  return LONG.format(parseIsoDate(iso))
}

/** « dimanche 23 août 2026 » */
export function formatFullDate(iso: string): string {
  return FULL.format(parseIsoDate(iso))
}
